
import { supabase } from "@/integrations/supabase/client";
import { ModuleLesson } from "@/types/supabase";
import { fetchLessonResources } from "./resourceService";

/**
 * Fetch all lessons for a module
 */
export const fetchModuleLessons = async (moduleId: string): Promise<ModuleLesson[]> => {
  const { data, error } = await supabase
    .from('module_lessons')
    .select('*')
    .eq('module_id', moduleId)
    .order('order_position', { ascending: true });

  if (error) {
    console.error('Error fetching lessons:', error);
    return [];
  }

  return data.map(lesson => ({
    ...lesson,
    taxonomy_level: lesson.taxonomy_level as ModuleLesson['taxonomy_level'],
    lesson_type: lesson.lesson_type as ModuleLesson['lesson_type'],
    learning_objectives: lesson.learning_objectives as string[] || []
  })) as ModuleLesson[];
};

// Alias for fetchModuleLessons for backward compatibility
export const fetchLessonsForModule = fetchModuleLessons;

/**
 * Fetch a specific lesson by ID
 */
export const fetchLessonById = async (lessonId: string): Promise<ModuleLesson | null> => {
  const { data, error } = await supabase
    .from('module_lessons')
    .select('*')
    .eq('id', lessonId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching lesson:', error);
    return null;
  }

  if (!data) return null;

  return {
    ...data,
    taxonomy_level: data.taxonomy_level as ModuleLesson['taxonomy_level'],
    lesson_type: data.lesson_type as ModuleLesson['lesson_type'],
    learning_objectives: data.learning_objectives as string[] || []
  } as ModuleLesson;
};

/**
 * Create a new lesson
 */
export const createLesson = async (lesson: Partial<Omit<ModuleLesson, 'id' | 'created_at' | 'updated_at'>>): Promise<ModuleLesson | null> => {
  if (!lesson.module_id || !lesson.title || !lesson.order_position) {
    console.error('Missing required lesson properties');
    return null;
  }

  const lessonToInsert = {
    module_id: lesson.module_id,
    title: lesson.title,
    description: lesson.description || null,
    content: lesson.content || null,
    taxonomy_level: lesson.taxonomy_level || 'understand',
    order_position: lesson.order_position,
    duration_minutes: lesson.duration_minutes || null,
    is_completed: lesson.is_completed || false,
    lesson_type: lesson.lesson_type || 'theoretical_framework',
    learning_objectives: lesson.learning_objectives || []
  };

  const { data, error } = await supabase
    .from('module_lessons')
    .insert(lessonToInsert)
    .select()
    .single();

  if (error) {
    console.error('Error creating lesson:', error);
    return null;
  }

  return {
    ...data,
    taxonomy_level: data.taxonomy_level as ModuleLesson['taxonomy_level'],
    lesson_type: data.lesson_type as ModuleLesson['lesson_type'],
    learning_objectives: data.learning_objectives as string[] || []
  } as ModuleLesson;
};

/**
 * Update an existing lesson
 */
export const updateLesson = async (lessonId: string, updates: Partial<ModuleLesson>): Promise<ModuleLesson | null> => {
  const { data, error } = await supabase
    .from('module_lessons')
    .update(updates)
    .eq('id', lessonId)
    .select()
    .single();

  if (error) {
    console.error('Error updating lesson:', error);
    return null;
  }

  return {
    ...data,
    taxonomy_level: data.taxonomy_level as ModuleLesson['taxonomy_level'],
    lesson_type: data.lesson_type as ModuleLesson['lesson_type'],
    learning_objectives: data.learning_objectives as string[] || []
  } as ModuleLesson;
};

/**
 * Delete a lesson
 */
export const deleteLesson = async (lessonId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('module_lessons')
    .delete()
    .eq('id', lessonId);

  if (error) {
    console.error('Error deleting lesson:', error);
    return false;
  }

  return true;
};

/**
 * Update lesson completion status
 */
export const updateLessonCompletionStatus = async (lessonId: string, isCompleted: boolean): Promise<boolean> => {
  const { error } = await supabase
    .from('module_lessons')
    .update({ is_completed: isCompleted })
    .eq('id', lessonId);

  if (error) {
    console.error('Error updating lesson completion status:', error);
    return false;
  }

  return true;
};

/**
 * Track user progress for a module
 */
export const updateUserProgress = async (moduleId: string, completionPercentage: number): Promise<boolean> => {
  const { data: user } = await supabase.auth.getUser();
  
  if (!user || !user.user) {
    console.error('No authenticated user found');
    return false;
  }
  
  const userId = user.user.id;
  
  // Check if progress record exists
  const { data: existingProgress, error: queryError } = await supabase
    .from('user_progress')
    .select()
    .eq('user_id', userId)
    .eq('module_id', moduleId)
    .maybeSingle();
    
  if (queryError) {
    console.error('Error checking user progress:', queryError);
    return false;
  }
  
  let error;
  
  if (existingProgress) {
    // Update existing record
    const completed_at = completionPercentage >= 100 ? new Date().toISOString() : existingProgress.completed_at;
    
    const { error: updateError } = await supabase
      .from('user_progress')
      .update({ 
        completion_percentage: completionPercentage,
        completed_at: completed_at
      })
      .eq('id', existingProgress.id);
      
    error = updateError;
  } else {
    // Create new record
    const completed_at = completionPercentage >= 100 ? new Date().toISOString() : null;
    
    const { error: insertError } = await supabase
      .from('user_progress')
      .insert({
        user_id: userId,
        module_id: moduleId,
        completion_percentage: completionPercentage,
        completed_at: completed_at
      });
      
    error = insertError;
  }
  
  if (error) {
    console.error('Error updating user progress:', error);
    return false;
  }
  
  return true;
};

/**
 * Calculate module completion percentage based on completed lessons
 */
export const calculateModuleCompletion = async (moduleId: string): Promise<number> => {
  const lessons = await fetchModuleLessons(moduleId);
  
  if (lessons.length === 0) return 0;
  
  const completedLessons = lessons.filter(lesson => lesson.is_completed).length;
  const completionPercentage = (completedLessons / lessons.length) * 100;
  
  return Math.round(completionPercentage);
};

/**
 * Function to track progress when a lesson is completed
 * Updated to expect two parameters instead of three
 */
export const trackLessonProgress = async (userId: string, lessonId: string, moduleId: string): Promise<boolean> => {
  try {
    // Mark this lesson as completed
    await updateLessonCompletionStatus(lessonId, true);
    
    // Calculate and update the overall module completion percentage
    const completionPercentage = await calculateModuleCompletion(moduleId);
    await updateUserProgress(moduleId, completionPercentage);
    
    return true;
  } catch (error) {
    console.error('Error tracking lesson progress:', error);
    return false;
  }
};

// Re-export the resource functions from resourceService to resolve the import errors
export { 
  fetchLessonResources, 
  createLessonResource,
  updateLessonResource,
  deleteLessonResource 
} from './resourceService';
